import clsx from 'clsx';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from '../../../../icons/animated';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footerContent?: React.ReactNode;
  width?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title = 'Drawer Title',
  children,
  footerContent,
  width = 'w-96',
}) => {
  const [isCloseHovered, setIsCloseHovered] = useState(false);

  return createPortal(
    <div
        className={clsx(
          'fixed inset-0 z-[9999] flex justify-end transition-all duration-300',
          isOpen
            ? 'opacity-100 visible'
            : 'opacity-0 invisible pointer-events-none',
        )}
    >
      { /* Overlay with backdrop blur */ }
      <div
          className={clsx(
            'absolute inset-0 bg-black transition-all duration-300',
            isOpen ? 'bg-opacity-50 backdrop-blur-sm' : 'bg-opacity-0',
          )}
          onClick={onClose}
      ></div>

      { /* Drawer Panel */ }
      <div
          className={clsx(
            'relative h-full max-w-full bg-white shadow-2xl flex flex-col',
            width,
            isOpen ? 'translate-x-0' : 'translate-x-full',
            'transition-transform duration-300 ease-out',
          )}
      >
        { /* Header with gradient */ }
        <div className='relative shrink-0 bg-gradient-to-r from-green-600 to-emerald-600 text-white overflow-hidden'>
          { /* Decorative background pattern */ }
          <div className='absolute inset-0 opacity-10'>
            <div className='absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-32 translate-x-32'></div>
            <div className='absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-24 -translate-x-24'></div>
          </div>

          { /* Header content */ }
          <div className='relative px-3 py-2.5 sm:px-4 flex items-center justify-between gap-3'>
            <div className='min-w-0'>
              <h2 className='truncate text-base font-bold tracking-tight sm:text-lg'>
                { title }
              </h2>
            </div>

            { /* Close button */ }
            <button
                onClick={onClose}
                onMouseEnter={() => setIsCloseHovered(true)}
                onMouseLeave={() => setIsCloseHovered(false)}
                className='group relative w-9 h-9 sm:w-10 sm:h-10 flex shrink-0 items-center justify-center rounded-xl bg-white bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 active:scale-95'
                aria-label='Close drawer'
            >
              <XIcon isHovered={isCloseHovered} size={20} className='text-white' />
            </button>
          </div>
          { /* Bottom border gradient */ }
          <div className='h-0.5 bg-gradient-to-r from-green-400 via-emerald-400 to-green-500'></div>
        </div>
        { /* Scrollable Content with custom scrollbar */ }
        <div className='flex-1 overflow-y-auto p-3 sm:p-4 bg-gradient-to-b from-gray-50 to-white custom-scrollbar'>
          { children }
        </div>
        { /* Footer with shadow */ }
        { footerContent && (
          <div className='shrink-0 p-3 sm:p-4 border-t border-gray-200 bg-white shadow-lg'>
            { footerContent }
          </div>
        ) }
      </div>
    </div>,
    document.body,
  );
};
